/**
 * Component: GuidelineListRow
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R03
 *
 * Description: Single-row render for the BROWSE-V1 list page. Shows
 *              the guideline name as primary text, the publish and
 *              update dates as secondary metadata, and (when
 *              present) an external-link icon linking to the
 *              upstream guideline URL.
 *
 *              The row itself navigates to
 *              `/browse/guidelines/{guidelineId}` when clicked
 *              (Phase 4 viewer). The external-link icon is a
 *              separate anchor that opens in a new tab and stops
 *              propagation so clicking it does not also trigger the
 *              row navigation.
 *
 * Risk Links: R9
 * FMEA Links: FM9
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { GuidelineListItem } from '../types';

interface GuidelineListRowProps {
  guideline: GuidelineListItem;
}

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function GuidelineListRow({
  guideline,
}: GuidelineListRowProps): React.ReactElement {
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    void navigate(`/browse/guidelines/${guideline.guidelineId}`);
  }, [navigate, guideline.guidelineId]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpen();
      }
    },
    [handleOpen],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      className="group flex flex-col gap-1 rounded-md border border-border bg-card p-4 text-left transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary"
      data-testid={`browse-row-${guideline.guidelineId}`}
      aria-label={`Open ${guideline.guidelineName}`}
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-base font-semibold leading-snug text-foreground">
          {guideline.guidelineName}
        </h3>
        {guideline.externalGuidelineURL && (
          <a
            href={guideline.externalGuidelineURL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center rounded p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={`Open ${guideline.guidelineName} on the external site in a new tab`}
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span>
          Published <time dateTime={guideline.publishDate}>{formatDate(guideline.publishDate)}</time>
        </span>
        <span>
          Updated <time dateTime={guideline.updateDate}>{formatDate(guideline.updateDate)}</time>
        </span>
      </div>
    </div>
  );
}
